<template>
  <div class="template-market">
    <!-- ==================== Hero：AI 设计入口为主 + 弱化搜索 ==================== -->
    <section class="hero">
      <div class="hero-inner">
        <p class="hero-eyebrow">THEME MARKETPLACE</p>
        <h1 class="hero-title">
          {{ $t('适用于任何项目的') }}<br />
          {{ $t('专业主题网站模板') }}
        </h1>
        <p class="hero-sub">
          {{ $t('根据自己的需求选择合适的模板，并在其基础上进行自定义和修改，以满足自己的品牌形象和功能要求。') }}
        </p>

        <!-- AI 设计入口（主视觉） -->
        <div class="ai-entry" @click="goAI">
          <div class="ai-text">
            <p class="ai-title">
              <v-icon small color="#FFD54F" class="mr-2">mdi-auto-fix</v-icon>
              {{ $t('用 AI 设计你的专属模板') }}
            </p>
            <p class="ai-desc">{{ $t('一句话描述你的店铺风格，AI 自动生成专属模板') }}</p>
            <div class="ai-tags">
              <span class="ai-tag"><v-icon x-small class="mr-1">mdi-check-circle</v-icon>{{ $t('无需设计经验') }}</span>
              <span class="ai-tag"><v-icon x-small class="mr-1">mdi-flash</v-icon>{{ $t('秒级生成') }}</span>
              <span class="ai-tag"><v-icon x-small class="mr-1">mdi-pencil-ruler</v-icon>{{ $t('可二次修改') }}</span>
            </div>
          </div>
          <v-btn color="#fff" class="ai-btn" rounded large elevation="2">
            <v-icon small class="mr-1" color="#7331FF">mdi-auto-fix</v-icon>
            <span class="ai-btn-text">{{ $t('立即 AI 设计') }}</span>
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <!-- 搜索框（弱化） -->
        <div class="hero-search">
          <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
          <input type="text" :placeholder="$t('搜索您想要的模版')" v-model="search_keyword"
            class="search-input" @keyup.enter="searchList()" />
          <v-btn icon small color="#9AA5B5" @click="searchList()">
            <v-icon size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <div class="hero-stats">
          <span class="stat-num">10W+</span>
          <span class="stat-label">{{ $t('注册卖家') }}</span>
          <span class="stat-dot"></span>
          <span class="stat-num">100+</span>
          <span class="stat-label">{{ $t('行业模板') }}</span>
          <span class="stat-dot"></span>
          <span class="stat-num">95%+</span>
          <span class="stat-label">{{ $t('满意度') }}</span>
        </div>
      </div>
    </section>

    <!-- ==================== 精选模板（多推荐） ==================== -->
    <section class="spotlight" v-if="spotlightList.length">
      <div class="section-inner">
        <div class="section-head">
          <h2 class="section-title">
            <v-icon color="#7331FF" class="mr-2">mdi-star-circle-outline</v-icon>
            {{ $t("精选模板") }}
          </h2>
        </div>
        <div class="spotlight-grid">
          <div class="t-card spotlight-item" v-for="s in spotlightList" :key="cardKey(s)" @click="goDetail(s)">
            <div class="t-cover">
              <div class="cover-box">
                <v-img :src="s.cover_img" alt="" class="cover-img" cover></v-img>
              </div>
              <div class="t-preview">
                <v-btn color="#7331FF" dark rounded>
                  <v-icon small class="mr-1">mdi-eye-outline</v-icon>
                  {{ $t('查看模板设计') }}
                </v-btn>
              </div>
              <span class="spotlight-badge" v-if="!s.price || s.price == 0">{{ $t('免费') }}</span>
              <v-img v-if="s.is_buy == 1" src="@/assets/images/template/icon-buyed.png" alt=""
                class="buyed-badge" width="36" height="40">
                <span class="buyed-text">buyed</span>
              </v-img>
            </div>
            <div class="spotlight-info">
              <p class="spotlight-name">{{ s.name }}</p>
              <p class="spotlight-desc">{{ s.des }}</p>
              <div class="spotlight-foot">
                <span class="spotlight-price" v-if="s.price && s.price != 0">{{ s.symbol }}<b>{{ s.price }}</b></span>
                <span class="spotlight-price free-text" v-else>{{ $t('免费') }}</span>
                <v-icon color="#7331FF">mdi-arrow-right-circle</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== 最新模板网格 ==================== -->
    <section class="section templates">
      <div class="section-inner">
        <div class="section-head">
          <h2 class="section-title">{{ $t("最新的主题和模板") }}</h2>
          <v-btn color="#7331FF" outlined rounded to="/template-list" class="more-btn">
            {{ $t('查看更多') }}
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <div class="grid" v-if="loading">
          <v-skeleton-loader v-for="i in 6" :key="'sk' + i" type="image, article" class="skeleton-card"></v-skeleton-loader>
        </div>

        <div class="grid" v-else-if="gridList.length">
          <div class="t-card" v-for="mitem in gridList" :key="cardKey(mitem)" @click="goDetail(mitem)">
            <div class="t-cover">
              <div class="cover-box">
                <v-img :src="mitem.cover_img" alt="" class="cover-img" cover></v-img>
              </div>
              <div class="t-preview">
                <v-btn color="#7331FF" dark rounded>
                  <v-icon small class="mr-1">mdi-eye-outline</v-icon>
                  {{ $t('查看模板设计') }}
                </v-btn>
              </div>
              <span class="free-tag" v-if="!mitem.price || mitem.price == 0">{{ $t('免费') }}</span>
              <v-img v-if="mitem.is_buy == 1" src="@/assets/images/template/icon-buyed.png" alt=""
                class="buyed-badge" width="36" height="40">
                <span class="buyed-text">buyed</span>
              </v-img>
            </div>
            <div class="t-info">
              <span class="t-name">{{ mitem.name }}</span>
              <span class="t-price" v-if="mitem.price && mitem.price != 0">{{ mitem.symbol }}<b>{{ mitem.price }}</b></span>
              <span class="t-price free-text" v-else>{{ $t('免费') }}</span>
            </div>
          </div>
        </div>

        <div class="empty-state" v-else>
          <v-icon size="52" color="#D0D5DD">mdi-image-off-outline</v-icon>
          <p>{{ $t('暂无模板，稍后再来看看吧') }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== 精选创作者 ==================== -->
    <section class="section creators">
      <div class="section-inner">
        <div class="section-head">
          <h2 class="section-title">{{ $t("本周精选创作者") }}</h2>
        </div>
        <div class="creator-grid" v-if="authorList.length">
          <div class="creator-card" v-for="n in authorList" :key="n.admin_id">
            <div class="creator-top">
              <div class="creator-info">
                <v-avatar size="52" class="creator-avatar">
                  <img :src="n.avatar" alt="alt">
                </v-avatar>
                <div class="creator-meta">
                  <p class="creator-name">{{ n.nickname }}</p>
                  <p class="creator-count">{{ n.themes_info.length }} {{ $t('个作品') }}</p>
                </div>
              </div>
              <v-btn color="#7331FF" outlined rounded small @click="goAuthor(n)">
                {{ $t('查看主页') }}
              </v-btn>
            </div>
            <div class="creator-themes">
              <div class="creator-theme" v-for="item in n.themes_info.slice(0, 2)" :key="item.themes_id"
                @click="goDetail(item)">
                <div class="cover-box">
                  <v-img :src="item.cover_img" alt="" class="cover-img" cover></v-img>
                </div>
                <div class="creator-theme-foot">
                  <span class="creator-theme-name">{{ item.name }}</span>
                  <span class="t-price" v-if="item.price && item.price != 0">{{ item.symbol }}<b>{{ item.price }}</b></span>
                  <span class="t-price free-text" v-else>{{ $t('免费') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>
          <v-progress-circular indeterminate color="#7331FF"></v-progress-circular>
        </div>
      </div>
    </section>

    <!-- ==================== 底部说明区 ==================== -->
    <section class="section perks">
      <div class="section-inner">
        <div class="perk-card">
          <img src="@/assets/images/template/icon-ad1.png" alt="" class="perk-img">
          <div>
            <p class="perk-title">{{ $t('数百万创意资源、无限下载') }}</p>
            <p class="perk-desc">{{ $t('立即订阅、可随时取消') }}</p>
          </div>
        </div>
        <div class="perk-card">
          <img src="@/assets/images/template/icon-ad2.png" alt="" class="perk-img">
          <div>
            <p class="perk-title">{{ $t('轻松的设计有您自己在线制作') }}</p>
            <p class="perk-desc">{{ $t('适合任何技能水平的智能模板') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== AI 设计引导弹窗 ==================== -->
    <v-dialog v-model="aiDialog" max-width="520" persistent>
      <div class="ai-dialog">
        <div class="ai-dialog-head">
          <v-icon color="#FFD54F" size="34">mdi-auto-fix</v-icon>
          <p class="ai-dialog-title">{{ $t('如何用 AI 设计模板') }}</p>
        </div>
        <div class="ai-steps">
          <div class="ai-step">
            <span class="ai-step-num">1</span>
            <p class="ai-step-text">{{ $t('描述你的店铺类型和风格，如“欧美简约服饰店”') }}</p>
          </div>
          <div class="ai-step">
            <span class="ai-step-num">2</span>
            <p class="ai-step-text">{{ $t('AI 自动生成多套模板设计供你选择') }}</p>
          </div>
          <div class="ai-step">
            <span class="ai-step-num">3</span>
            <p class="ai-step-text">{{ $t('选中模板后一键启用，随时调整细节') }}</p>
          </div>
        </div>
        <div class="ai-dialog-foot">
          <v-btn color="#7331FF" dark rounded to="/template-list" @click="aiDialog = false">
            {{ $t('去模板列表看看') }}
          </v-btn>
          <v-btn text rounded @click="aiDialog = false">{{ $t('我知道了') }}</v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "Home",
  data() {
    return {
      mobanlist: [],
      getparam: {
        current_page: 1,
        page_number: 50
      },
      authorList: [],
      search_keyword: '',
      loading: false,
      aiDialog: false
    }
  },
  computed: {
    // 精选模板：3 排 × 4 = 12 个
    spotlightList() {
      return this.mobanlist.slice(0, 12);
    },
    // 网格：精选之后的全部模板
    gridList() {
      return this.mobanlist.slice(12);
    }
  },
  mounted() {
    this.getlist();
    this.getAuthorList();
  },
  methods: {
    getlist() {
      this.loading = true;
      this.$api.template.list(this.getparam).then(res => {
        this.loading = false;
        if (res.status) {
          this.mobanlist = res.data.records;
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    getAuthorList() {
      this.$api.template.author_list({ current_page: 1, page_number: 2 }).then(res => {
        if (res.status) {
          this.authorList = res.data.records || [];
        }
      })
    },
    searchList() {
      const keyword = (this.search_keyword || '').trim();
      this.$router.push('/template-list?keyword=' + encodeURIComponent(keyword));
    },
    // 市场里同时有官方模板和店主发布的模板：
    // 官方用 themes_id，店主发布的用 custom_themes_id，两者不能混。
    // 用 themes_id 做 v-for 的 key 时，自定义模板的 themes_id 都是 0，会互相覆盖。
    cardKey(item) {
      return item && item.source === 'custom'
        ? 'c' + item.custom_themes_id
        : 'o' + (item ? item.themes_id : '');
    },
    goDetail(item) {
      if (item && item.source === 'custom') {
        this.$router.push('/template-detail?custom_themes_id=' + item.custom_themes_id);
        return;
      }
      this.$router.push('/template-detail?themes_id=' + item.themes_id);
    },
    goAuthor(n) {
      this.$router.push('/template-list?author_id=' + n.admin_id);
    },
    goAI() {
      this.aiDialog = true;
    }
  },
};
</script>
<style lang="scss" scoped>
/* ===================== 通用容器 ===================== */
.hero .hero-inner,
.section .section-inner,
.spotlight .section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===================== 封面图尺寸约束（9:16 竖版） ===================== */
.cover-box {
  position: relative;
  width: 100%;
  padding-top: 177.78%; /* 9:16 */
  overflow: hidden;

  .cover-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

/* ===================== 页面底色（与白色导航区分） ===================== */
.template-market {
  background: #F5F2FB;
}

/* ===================== Hero ===================== */
.hero {
  background: #F5F2FB;
  padding: 72px 0 48px;
  text-align: center;

  .hero-eyebrow {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 3px;
    color: #7331FF;
    text-transform: uppercase;
    margin-bottom: 18px;
  }

  .hero-title {
    font-size: 40px;
    line-height: 1.3;
    font-weight: 700;
    color: #0F3460;
    letter-spacing: 0.5px;
    margin-bottom: 20px;
  }

  .hero-sub {
    font-size: 15px;
    line-height: 1.8;
    color: #5E6B7E;
    max-width: 560px;
    margin: 0 auto 36px;
  }

  /* AI 设计入口卡片 */
  .ai-entry {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    max-width: 720px;
    margin: 0 auto 28px;
    padding: 26px 32px;
    background: linear-gradient(135deg, #7331FF 0%, #9B5CFF 60%, #B07CFF 100%);
    border-radius: 16px;
    cursor: pointer;
    text-align: left;
    box-shadow: 0 12px 32px rgba(115, 49, 255, 0.25);
    transition: transform 0.25s, box-shadow 0.25s;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 18px 40px rgba(115, 49, 255, 0.32);
    }

    .ai-title {
      font-size: 19px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
    }

    .ai-desc {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 12px;
    }

    .ai-tags {
      display: flex;
      gap: 14px;
      flex-wrap: wrap;
    }

    .ai-tag {
      display: inline-flex;
      align-items: center;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.92);
      background: rgba(255, 255, 255, 0.16);
      padding: 3px 10px;
      border-radius: 16px;
    }

    .ai-btn {
      flex-shrink: 0;
      text-transform: none;
      font-weight: 700;

      .ai-btn-text {
        color: #7331FF;
      }
    }
  }

  /* 弱化搜索框 */
  .hero-search {
    display: flex;
    align-items: center;
    background: #F6F8FC;
    border: 1px solid #E8ECF2;
    border-radius: 24px;
    padding: 4px 6px 4px 14px;
    max-width: 380px;
    margin: 0 auto 44px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus-within {
      border-color: #C9B8F2;
      box-shadow: 0 0 0 3px rgba(115, 49, 255, 0.06);
      background: #fff;
    }

    .search-icon {
      color: #B4BDC9;
      margin-right: 8px;
    }

    .search-input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 13px;
      color: #242939;
      background: transparent;
      height: 30px;

      &::placeholder {
        color: #B4BDC9;
      }
    }
  }

  .hero-stats {
    display: flex;
    align-items: center;
    justify-content: center;

    .stat-num {
      font-size: 20px;
      font-weight: 700;
      color: #0F3460;
      margin-right: 8px;
    }

    .stat-label {
      font-size: 13px;
      color: #8A96A8;
      margin-right: 24px;
    }

    .stat-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #D0D5DD;
      margin-right: 24px;
    }
  }
}

@media (max-width: 960px) {
  .hero {
    padding: 48px 0 32px;

    .hero-title {
      font-size: 26px;
    }

    .ai-entry {
      flex-direction: column;
      align-items: flex-start;
      padding: 20px;
    }
  }
}

/* ===================== 精选模板（多推荐 3 张） ===================== */
.spotlight {
  background: #fff;
  padding: 56px 0;

  .spotlight-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 960px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .spotlight-item {
    display: flex;
    flex-direction: column;
  }

  .spotlight-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255, 255, 255, 0.94);
    color: #7331FF;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 3;
  }

  .spotlight-info {
    padding: 14px 16px 16px;
    display: flex;
    flex-direction: column;
    flex: 1;

    .spotlight-name {
      font-size: 16px;
      font-weight: 600;
      color: #242939;
      margin-bottom: 6px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .spotlight-desc {
      font-size: 12px;
      line-height: 1.6;
      color: #8A96A8;
      margin-bottom: 12px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .spotlight-foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: auto;
    }

    .spotlight-price {
      font-size: 14px;
      color: #7331FF;
      font-weight: 600;

      b {
        font-size: 18px;
      }

      &.free-text {
        color: #09bb07;
      }
    }
  }
}

/* ===================== 通用区块 ===================== */
.section {
  padding: 64px 0;

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;

    .section-title {
      font-size: 24px;
      font-weight: 700;
      color: #0F3460;
      margin-bottom: 0;
    }

    .more-btn {
      text-transform: none;
      font-weight: 600;
    }
  }
}

.templates {
  background: #F5F2FB;
}

.creators {
  background: #fff;
}

/* ===================== 模板网格 ===================== */
.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.skeleton-card {
  border-radius: 12px;
}

.t-card {
  cursor: pointer;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #EEF1F5;
  transition: box-shadow 0.25s, transform 0.25s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(15, 52, 96, 0.12);

    .cover-img {
      transform: scale(1.04);
    }

    .t-preview {
      opacity: 1;
    }
  }
}

.t-cover {
  position: relative;
  margin: 8px;

  .cover-box {
    border-radius: 8px;
  }

  .cover-img {
    border-radius: 8px;
    transition: transform 0.4s ease;
  }

  .t-preview {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 52, 96, 0.22);
    opacity: 0;
    transition: opacity 0.25s;
    z-index: 2;
    border-radius: 8px;
  }

  .free-tag {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255, 255, 255, 0.94);
    color: #09bb07;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 3;
  }

  .buyed-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;

    .buyed-text {
      bottom: 5px;
      position: absolute;
      left: 0;
      width: 100%;
      text-align: center;
      right: 0;
      color: #fff;
      transform: scale(0.75);
    }
  }
}

.t-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 16px;

  .t-name {
    font-size: 15px;
    font-weight: 600;
    color: #242939;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 12px;
  }

  .t-price {
    font-size: 14px;
    color: #7331FF;
    font-weight: 600;
    flex-shrink: 0;

    b {
      font-size: 16px;
    }

    &.free-text {
      color: #09bb07;
    }
  }
}

/* ===================== 空状态 ===================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 0;
  color: #9AA5B5;
  font-size: 14px;

  p {
    margin: 12px 0 0;
  }
}

/* ===================== 创作者 ===================== */
.creator-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.creator-card {
  background: #fff;
  border: 1px solid #EEF1F5;
  border-radius: 14px;
  padding: 20px;
  transition: box-shadow 0.25s, transform 0.25s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(15, 52, 96, 0.1);
  }

  .creator-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .creator-info {
    display: flex;
    align-items: center;
  }

  .creator-avatar {
    border: 2px solid #7331FF;
    margin-right: 12px;
  }

  .creator-name {
    font-size: 15px;
    font-weight: 600;
    color: #242939;
    margin-bottom: 2px;
  }

  .creator-count {
    font-size: 12px;
    color: #9AA5B5;
    margin-bottom: 0;
  }

  .creator-themes {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .creator-theme {
    border: 1px solid #EEF1F5;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 8px 20px rgba(15, 52, 96, 0.1);
    }

    .creator-theme-foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 10px;

      .creator-theme-name {
        font-size: 13px;
        font-weight: 500;
        color: #242939;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-right: 8px;
      }
    }
  }
}

/* ===================== 底部说明区 ===================== */
.perks {
  background: #F5F2FB;

  .section-inner {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    max-width: 900px;
  }

  .perk-card {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    border: 1px solid #EEF1F5;
    border-radius: 12px;
    padding: 24px;

    .perk-img {
      width: 72px;
      flex-shrink: 0;
    }

    .perk-title {
      font-size: 16px;
      font-weight: 600;
      color: #242939;
      margin-bottom: 4px;
    }

    .perk-desc {
      font-size: 13px;
      color: #8A96A8;
      margin-bottom: 0;
    }
  }

  @media (max-width: 960px) {
    .section-inner {
      grid-template-columns: 1fr;
    }
  }
}

/* ===================== AI 引导弹窗 ===================== */
.ai-dialog {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  text-align: center;

  .ai-dialog-head {
    margin-bottom: 24px;

    .ai-dialog-title {
      font-size: 20px;
      font-weight: 700;
      color: #0F3460;
      margin: 10px 0 0;
    }
  }

  .ai-steps {
    text-align: left;
    margin-bottom: 28px;
  }

  .ai-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ai-step-num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #7331FF;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .ai-step-text {
    font-size: 14px;
    color: #3D4757;
    line-height: 1.6;
    margin-bottom: 0;
  }

  .ai-dialog-foot {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
}
</style>
